import axios from "axios";
import snackBarUtil from "./snackBarUtil";
import { errorRef, setErrorRef } from "index";

export interface ApiResource {
  URL: string;
  METHOD?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
}

interface Query {
  [key: string]: string | number | boolean | string[] | number[];
}

export interface ApiServiceInterface {
  resource: ApiResource;
  params?: Query;
  pathVars?: Query;
  data?: any;
  noAuth?: boolean;
  betfair?: boolean;
  headers?: any;
  customToken?: string;
}

interface Error {
  message: string[];
}

interface ApiResponse {
  response: any;
  error: Error | null;
}

const filterQuery = (query: Query) => {
  const newQuery: Query = {};
  Object.keys(query).forEach((item) => {
    if (Array.isArray(query[item])) {
      newQuery[item] = query[item].toString();
    } else {
      newQuery[item] = query[item];
    }
  });
  return newQuery;
};

const apiService: (arg: ApiServiceInterface) => Promise<any> = async ({
  resource,
  data = {},
  params = {},
  headers = {},
  pathVars = {},
  noAuth = false,
  customToken,
}) => {
  const { METHOD, URL } = resource;
  const token = localStorage.getItem("token");

  let url = URL;


  Object.keys(pathVars).forEach((key) => {
    if (url) url = url.replace(":" + key, pathVars[key].toString());
  });
  let config;
  // const baseURL = process.env.REACT_APP_API_BASEURL;
  const baseURL: string = process.env.REACT_APP_PROD === "true"
  ? `${window.location.origin}/api/v1`
  : (process.env.REACT_APP_API_BASEURL as string);

  

  try {
    config = {
      method: METHOD || "GET",
      baseURL,
      url,
      data,
      params: filterQuery(params),
      headers: {
        ...headers,
        ...(Boolean(token && !noAuth)
          ? { Authorization: `Bearer ${customToken || token}` }
          : {}),
      },
    };
  } catch (errors: any) {
    console.log(errors?.response, "error");
  }

  if (config) {
    return axios(config);
  }
};

const handleApiError = (error: any) => {
  if (error.message === "Network Error") {
    snackBarUtil.error("Network error. Please check your connection.");
    if (errorRef && setErrorRef) {
      setErrorRef(true);
    }
  }else if (error?.message?.includes("CORS")) {
    snackBarUtil.error(
      "CORS error detected. Please ensure the server allows requests from this origin."
    );
    localStorage.removeItem("token");
    localStorage.removeItem("adminDetails");
    localStorage.removeItem("adminRefreshToken");
    localStorage.removeItem("operatorId");
    localStorage.removeItem("userId");
    localStorage.removeItem("isNotification");
    localStorage.removeItem("notifications");
    localStorage.removeItem("columnsDateState");
    window.location.replace("/login");
  }  else if (error.response) {
    const { status, data } = error.response;

    switch (status) {
      case 401:
        snackBarUtil.error("Unauthorized access. Redirecting to login.");
        localStorage.removeItem("token");
        localStorage.removeItem("adminDetails");
        localStorage.removeItem("adminRefreshToken");
        localStorage.removeItem("operatorId");
        localStorage.removeItem("userId");
        localStorage.removeItem("isNotification");
        localStorage.removeItem("notifications");
        localStorage.removeItem("columnsDateState");
        window.location.replace("/login");
        break;

      case 502:
        snackBarUtil.error("Server is temporarily unavailable. Please log in again.");
        localStorage.removeItem("token");
        localStorage.removeItem("adminDetails");
        localStorage.removeItem("adminRefreshToken");
        localStorage.removeItem("operatorId");
        localStorage.removeItem("userId");
        localStorage.removeItem("isNotification");
        localStorage.removeItem("notifications");
        localStorage.removeItem("columnsDateState");
        window.location.replace("/login");
        break;

      default:
        snackBarUtil.error(data?.message || "An unexpected error occurred.");
        if (errorRef && setErrorRef) {
          setErrorRef(false);
        }
        break;
    }
  } 
};
const apiHandler: (arg: ApiServiceInterface) => Promise<ApiResponse> = async (
  args
) => {
  let result: any = {};
  await apiService(args)
    .then((response) => {
      if (response) {
        if (errorRef && setErrorRef) {
          setErrorRef(false);
        }

        if (!response?.status) {
          result["response"] = response;
          if (response?.logout) {
            localStorage.removeItem("token");
            localStorage.removeItem("adminDetails");
            localStorage.removeItem("adminRefreshToken");
            localStorage.removeItem("operatorId");
            localStorage.removeItem("userId");
            localStorage.removeItem("isNotification");
            localStorage.removeItem("notifications");
            localStorage.removeItem("columnsDateState");
            window.location.replace("/login");
          }
          
        } else {
          result["response"] = response;
        }
      }
    })
    .catch((error) => {
      result["error"] = error.response?.data;
      result.error = error.response?.data || { message: ["Unexpected error occurred."] };
    handleApiError(error);
    });

  return result;
};

const apiSnackbarNotifications: (
  arg: ApiResponse
) => Promise<ApiResponse> = async (args) => {
  if (args.error) {
    // eslint-disable-next-line
    const { message } = args.error;
    // if (Array.isArray(message)) {
    //   message.forEach((msg) => {
    //     if (typeof msg === "string") {
    //       snackBarUtil.error(msg);
    //     } else {
    //       console.warn("Invalid message type in error array:", msg);
    //     }
    //   });
    // } else if (typeof message === "string") {
    //   snackBarUtil.error(message);
    // } else {
    //   console.warn("Unexpected error message format:", message);
    // }
  }


  // if (!args?.response?.status) {
  //   const errorMsg = args?.response?.msg;
  //   if (typeof errorMsg === "string") {
  //     snackBarUtil.error(errorMsg);
  //   } else {
  //     console.warn("Invalid message in response:", errorMsg);
  //   }
  // } else if (typeof args?.response?.message === "string") {
  //   snackBarUtil.success(args.response.message);
  // } else {
  //   console.warn("Unexpected response message format:", args.response?.message);
  // }

  return args;
};

const apiWithSnackbar: (
  arg: ApiServiceInterface
) => Promise<ApiResponse> = async (args) => {
  const result = await apiHandler(args);
  return apiSnackbarNotifications(result);
};

const apiWithErrorSnackbar: (
  arg: ApiServiceInterface
) => Promise<ApiResponse> = async (args) => {
  const result = await apiHandler(args);
  if (result?.error) {
    const { message } = result.error;
    if (typeof message === "object") {
      message.forEach((msg) => {
        if (typeof msg === "string") {
          snackBarUtil.error(msg);
        }
      });
    } else {
      snackBarUtil.error(message);
    }
  }
  return result;
};

export { apiService, apiHandler, apiWithErrorSnackbar, apiWithSnackbar };
